import React, {useRef, useEffect, useCallback, useState} from "react";
import {Trash2, Edit, Loader, Table2, MapPin, Users} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, AddAction, ListLoader} from "./SettingsUI";

const TablesList = ({
                        searchTerm,
                        setSearchTerm,
                        debouncedSearchTerm,
                        selectedItems,
                        setSelectedItems,
                        selectedAll,
                        setSelectedAll,
                        showNotification,
                        onCreateTable,
                        onEditTable
                    }) => {
    const queryClient = useQueryClient();
    const loadingRef = useRef(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState([]);


    // Function to fetch tables from the API with pagination
    const fetchTables = async ({pageParam = 1}) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            let url = `/tables?page=${pageParam}`;
            if (debouncedSearchTerm.trim()) {
                url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            return {
                data: response.data.data || [],
                currentPage: response.data.pagination?.currentPage || 1,
                totalPages: response.data.pagination?.totalPages || 1,
                hasNextPage: response.data.pagination?.currentPage < response.data.pagination?.totalPages
            };
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des tables";
            throw new Error(errorMessage);
        }
    };

    // Function to delete tables
    const deleteTables = async (tableIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const results = await Promise.all(
                tableIds.map(id =>
                    api.delete(`/tables/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === tableIds.length) {
                    throw new Error("Impossible de supprimer les tables sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${tableIds.length} tables n'ont pas pu être supprimées`);
                }
            }

            return tableIds;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des tables";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteTables,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['tables']});
            showNotification('success', 'Tables supprimées avec succès');
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        }
    });

    // Use react-query to fetch and cache the data with infinite query
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error
    } = useInfiniteQuery({
        queryKey: ['tables', debouncedSearchTerm],
        queryFn: fetchTables,
        getNextPageParam: (lastPage) => {
            return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined;
        },
    });

    // Flatten the pages data into a single tables array
    const tables = data?.pages.flatMap(page => page.data) || [];

    // Function to load more data
    const loadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Implement Intersection Observer for infinite scrolling
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            {threshold: 0.5}
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => {
            if (loadingRef.current) {
                observer.unobserve(loadingRef.current);
            }
        };
    }, [loadMore]);

    // Reset when search term changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? tables.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (tableId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(tableId)
                ? prevSelected.filter((id) => id !== tableId)
                : [...prevSelected, tableId]
        );
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => {
        setItemsToDelete(ids);
        setShowConfirmDialog(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = () => {
        deleteMutation.mutate(itemsToDelete);
    };

    // Handle delete cancellation
    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
        setItemsToDelete([]);
    };

    const rowActions = (table) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditTable(table), tone: 'blue'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([table.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (table) => (
            <span className="flex items-center gap-1.5 min-w-0">
                <Table2 className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400"/>
                <span className="truncate">{table.name}</span>
            </span>
        )},
        {key: 'area', label: 'Zone', render: (table) => (
            <span className="inline-flex items-center gap-1 min-w-0">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500"/>
                <span className="truncate">{table.area?.name || '-'}</span>
            </span>
        )},
        {key: 'capacity', label: 'Capacité', render: (table) => (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                <Users className="w-3 h-3 mr-1"/>
                {table.capacity}
            </span>
        )},
        {key: 'shape', label: 'Forme', className: 'capitalize', render: (table) => table.shape || '-'},
    ];

    return (
        <div>
            <AddAction onClick={onCreateTable}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher une table..."/>

            <SelectionBar count={selectedItems.length} singular="table sélectionnée" plural="tables sélectionnées"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading && !data ? (
                <ListLoader label="Chargement des tables..."/>
            ) : error && !data ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={tables}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditTable}
                    empty={{
                        icon: Table2,
                        title: debouncedSearchTerm ? 'Aucune table trouvée' : 'Aucune table',
                        text: debouncedSearchTerm ? 'Ajustez la recherche.' : 'Créez les tables de vos zones.',
                    }}
                >
                    {(hasNextPage || isFetchingNextPage) && (
                        <div ref={loadingRef} className="flex justify-center items-center py-4">
                            <Loader className="w-6 h-6 text-primary animate-spin"/>
                        </div>
                    )}
                </SettingsTable>
            )}

            {showConfirmDialog && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${itemsToDelete.length} table${itemsToDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    danger={true}
                />
            )}
        </div>
    );
};

export default TablesList;
